import React from 'react';

import EmptyGlossarySection from '../../glossary/EmptyGlossarySection';
import GlossaryEntitiesList from '../../glossary/GlossaryEntitiesList';
import GlossaryActions from './GlossaryActions';

import { EntityType, GlossaryNode, GlossaryTerm } from '../../../types.generated';
import { useBatchRemoveEntitiesMutation } from '../../../graphql/mutations.generated';

import { bulkDeleteGlossaryEntities } from './utils/bulkDeleteGlossaryEntities';
import { useEntityData, useRefetch } from '../shared/EntityContext';
import { useEntityRegistry } from '../../useEntityRegistry';
import { usePagination } from '../../shared/usePagination';
import { sortGlossaryTerms } from '../glossaryTerm/utils';
import { sortGlossaryNodes } from './utils';

function ChildrenTab() {
    const { entityData } = useEntityData();
    const entityRegistry = useEntityRegistry();

    if (!entityData) return <></>;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [batchRemoveGlossaryMutation] = useBatchRemoveEntitiesMutation();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const refetch = useRefetch();

    const childNodes = entityData?.children?.relationships
        .filter((child) => child.entity?.type === EntityType.GlossaryNode)
        .sort((nodeA, nodeB) => sortGlossaryNodes(entityRegistry, nodeA.entity, nodeB.entity))
        .map((child) => child.entity);
    const childTerms = entityData?.children?.relationships
        .filter((child) => child.entity?.type === EntityType.GlossaryTerm)
        .sort((termA, termB) => sortGlossaryTerms(entityRegistry, termA.entity, termB.entity))
        .map((child) => child.entity);

    const combineChilds = [...(childNodes as GlossaryNode[]), ...(childTerms as GlossaryTerm[])];

    const hasTermsOrNodes = !!childNodes?.length || !!childTerms?.length;

    const {
        page,
        pageSize,
        selectedItems,
        isAllSelected,
        isIndeterminate,
        handleSelectAll,
        setSelectedItems,
        handleChangePage,
        handleShowSizeChange,
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = usePagination({
        items: combineChilds.map((child) => child.urn || ''),
    });

    /**
     * Handles the bulk deletion of the selected glossary terms.
     */
    const handleBulkDelete = async () => {
        bulkDeleteGlossaryEntities({
            nodes: childNodes as GlossaryNode[],
            terms: childTerms as GlossaryTerm[],
            selectedItems,
            batchRemoveGlossaryMutation,
            setSelectedItems,
            refetch,
            onSuccess: () => {
                // Reset the page to the first page after deleting the entities.
                handleChangePage(1);
            },
        });
    };

    if (hasTermsOrNodes) {
        return (
            <>
                <GlossaryActions
                    nodes={(childNodes as GlossaryNode[]) || []}
                    terms={(childTerms as GlossaryTerm[]) || []}
                    selectedItems={selectedItems}
                    isAllSelected={isAllSelected}
                    isIndeterminate={isIndeterminate}
                    handleSelectAll={handleSelectAll}
                    handleBulkDelete={handleBulkDelete}
                    setSelectedItems={setSelectedItems}
                />
                <GlossaryEntitiesList
                    page={page}
                    pageSize={pageSize}
                    selectedItems={selectedItems}
                    onChangePage={handleChangePage}
                    setSelectedItems={setSelectedItems}
                    onShowSizeChange={handleShowSizeChange}
                    onBulkDeleteEntities={handleBulkDelete}
                    nodes={(childNodes as GlossaryNode[]) || []}
                    terms={(childTerms as GlossaryTerm[]) || []}
                />
            </>
        );
    }

    return <EmptyGlossarySection description="No Terms or Term Groups" />;
}

export default ChildrenTab;
